import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { FileSpreadsheet, FileText, FileDown, Users, Package, CalendarClock, DollarSign, Loader2, Building2, FileSignature, Receipt, AlertTriangle, UserCheck, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';
import InstallmentReports from './InstallmentReports';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const REPORTS = [
  { key: 'customers', title: 'Customer Report', desc: 'All customers with contact and account info', icon: Users, color: 'text-blue-500' },
  { key: 'user_wise', title: 'User-wise Customer Report', desc: 'Customers grouped by assigned Sales / Recovery officer', icon: UserCheck, color: 'text-cyan-400' },
  { key: 'inventory', title: 'Inventory Report', desc: 'Stock items across all categories, prices, profit and status', icon: Package, color: 'text-purple-500' },
  { key: 'installments', title: 'Installment Report', desc: 'Active plans with paid/remaining amounts', icon: CalendarClock, color: 'text-amber-500' },
  { key: 'agreements', title: 'Agreement Report', desc: 'All agreements with status, plan and registration date', icon: FileSignature, color: 'text-pink-500' },
  { key: 'collection', title: 'Collection Report', desc: 'Every receipt/payment with amount, late fee and waivers', icon: Receipt, color: 'text-emerald-500' },
  { key: 'recovery', title: 'Recovery Report', desc: 'Outstanding installments needing recovery', icon: DollarSign, color: 'text-emerald-500' },
  { key: 'outstanding', title: 'Outstanding / Overdue Report', desc: 'Every agreement with remaining > 0', icon: AlertTriangle, color: 'text-red-500' },
  { key: 'performance', title: 'Branch Performance', desc: 'Recovery %, total billed vs recovered, outstanding, defaults', icon: BarChart3, color: 'text-amber-500' },
];

const FORMATS = [
  { key: 'pdf', label: 'PDF', icon: FileText, color: 'bg-red-600 hover:bg-red-700' },
  { key: 'xlsx', label: 'Excel', icon: FileSpreadsheet, color: 'bg-emerald-600 hover:bg-emerald-700' },
  { key: 'csv', label: 'CSV', icon: FileDown, color: 'bg-blue-600 hover:bg-blue-700' },
];

export default function ReportsCenter() {
  const { user } = useAuth();
  // HQ roles (Super Admin / Admin) can see & filter every branch. Every other
  // role (Branch Manager included) is hard-locked to their own branch.
  const isHQ = user?.role === 'Super Admin' || user?.role === 'Admin';
  const [downloading, setDownloading] = useState(null);
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState(isHQ ? 'all' : (user?.branch_id || ''));

  // Read `?tab=installments&status=overdue` so dashboard cards can deep-link.
  const location = useLocation();
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const initialTab    = query.get('tab') === 'installments' ? 'installments' : 'standard';
  const initialStatus = ['overdue', 'due_today', 'upcoming', 'all'].includes(query.get('status'))
    ? query.get('status') : 'overdue';

  useEffect(() => {
    axios.get(`${API_URL}/api/branches`, { withCredentials: true })
      .then(({ data }) => setBranches(data))
      .catch(() => setBranches([]));
  }, []);

  // Force-lock non-HQ users to their assigned branch every time it changes,
  // even if some code path tries to widen the scope.
  useEffect(() => {
    if (!isHQ && user?.branch_id && branchId !== user.branch_id) {
      setBranchId(user.branch_id);
    }
  }, [isHQ, user?.branch_id, branchId]);

  const myBranchName = useMemo(() => {
    if (isHQ) return null;
    return branches.find(b => b.id === (user?.branch_id))?.name || user?.branch_name || 'My Branch';
  }, [isHQ, branches, user?.branch_id, user?.branch_name]);

  const handleDownload = async (report, fmt) => {
    try {
      setDownloading(`${report}-${fmt}`);
      // Backend also enforces this via _resolve_branch_scope; the FE query is
      // an alignment, not the security boundary.
      const effectiveBranch = isHQ ? branchId : (user?.branch_id || '');
      const params = effectiveBranch && effectiveBranch !== 'all' ? `?branch_id=${encodeURIComponent(effectiveBranch)}` : '';
      const response = await axios.get(`${API_URL}/api/reports/${report}/${fmt}${params}`, {
        withCredentials: true, responseType: 'blob',
      });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      const ts = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      const branchTag = effectiveBranch && effectiveBranch !== 'all' ? `_${branches.find(b => b.id === effectiveBranch)?.name?.replace(/\s+/g, '') || 'branch'}` : '';
      a.download = `${report}${branchTag}_${ts}.${fmt}`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`${report} exported as ${fmt.toUpperCase()}`);
    } catch (error) {
      toast.error('Download failed');
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="reports-center">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-3 md:space-y-0">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2" data-testid="reports-title">Reports Center</h1>
          <p className="text-slate-400">Export every part of your ERP as PDF, Excel, or CSV — branch-wise or global</p>
        </div>
        <div className="flex items-center space-x-2">
          <Building2 className="h-4 w-4 text-amber-500" />
          <Label className="text-slate-300 text-sm">Branch:</Label>
          {isHQ ? (
            <Select value={branchId} onValueChange={setBranchId}>
              <SelectTrigger className="bg-slate-800 border-slate-700 text-white w-48" data-testid="report-branch-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700 text-white">
                <SelectItem value="all">All Branches</SelectItem>
                {branches.map((b) => (
                  <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <div data-testid="report-branch-locked"
              className="w-48 h-9 px-3 flex items-center rounded-sm bg-slate-800 border border-slate-700 text-white text-sm cursor-not-allowed opacity-90 select-none"
              title="Restricted to your assigned branch">
              {myBranchName}
            </div>
          )}
        </div>
      </div>

      <Tabs defaultValue={initialTab} className="w-full">
        <TabsList className="bg-slate-900 border border-slate-800">
          <TabsTrigger value="standard" data-testid="reports-tab-standard"
            className="data-[state=active]:bg-slate-800 data-[state=active]:text-white text-slate-400">
            Standard Reports
          </TabsTrigger>
          <TabsTrigger value="installments" data-testid="reports-tab-installments"
            className="data-[state=active]:bg-slate-800 data-[state=active]:text-white text-slate-400">
            Due / Upcoming / Overdue
          </TabsTrigger>
        </TabsList>

        <TabsContent value="standard" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {REPORTS.map((r) => (
              <Card key={r.key} className="bg-slate-900 border-slate-800 hover:-translate-y-1 transition-transform" data-testid={`report-card-${r.key}`}>
                <CardHeader className="flex flex-row items-start justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`p-3 rounded-sm bg-slate-800 ${r.color}`}>
                      <r.icon className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white">{r.title}</h3>
                      <p className="text-sm text-slate-400">{r.desc}</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center space-x-2">
                    {FORMATS.map((f) => (
                      <Button
                        key={f.key}
                        onClick={() => handleDownload(r.key, f.key)}
                        disabled={downloading === `${r.key}-${f.key}`}
                        data-testid={`download-${r.key}-${f.key}`}
                        className={`${f.color} text-white flex-1`}
                      >
                        {downloading === `${r.key}-${f.key}` ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <f.icon className="h-4 w-4 mr-2" />
                        )}
                        {f.label}
                      </Button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="installments" className="mt-6">
          <InstallmentReports initialStatus={initialStatus} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
